import { useContext, useEffect, useState } from "react";
import axios from "axios";
import constate from "constate";
import * as yup from "yup";
import { useHistory } from "react-router-dom";

import { SettingsContext } from '../contexts/index.jsx';
import { steps, templates } from "./constants";
import { useGetLookbook, useUpsertLookbookApi } from "./useLookbookApi";
import { downloadLookbook } from "./helpers";

const highlighSchema = yup.object().shape({
  images: yup
    .array()
    .of(yup.number())
    .min(5),
  material: yup.string().required(),
  diameter: yup.string().required(),
  date: yup.string().required(),
  price: yup.number().required(),
});

const stepsOrder = [
  steps.LISTINGS,
  steps.PRODUCTS,
  steps.HIGHLIGHTS,
  steps.COVER,
  steps.PREVIEW,
];

const getValue = (listing, sort) => {
  const [key] = sort.split("_");

  switch (key) {
    case "brand":
      return listing?.product?.brand?.name;
    case "diameter":
      return (
        listing?.product?.values?.case_diameter?.value ||
        listing?.values?.case_diameter
      );
    case "price":
      return listing.price;
    case "material":
      return (
        listing?.product?.values?.case_material?.value ||
        listing?.values?.case_material
      );
    default:
      return null;
  }
};

const [UpsertLookbookProvider, useUpsertLookbook] = constate(({ id }) => {
  const history = useHistory();
  const { iframeQueryParams } = useContext(SettingsContext);

  const { upsertLookbook } = useUpsertLookbookApi();

  const initialStep = id === "new" ? steps.LISTINGS : steps.PREVIEW;
  const [currentStep, setCurrentStep] = useState(initialStep);
  const { lookbook } = useGetLookbook(id, currentStep === steps.PREVIEW);
  const [lastStep, setLastStep] = useState(initialStep);
  const [initialized, setInitialized] = useState(id === "new");

  const [listings, setListings] = useState({});
  const [sort, setSort] = useState(null);
  const [listingsOrder, setListingsOrder] = useState([]);
  const [settings, setSettings] = useState({
    hideBrands: false,
    hideComments: false,
    hidePrices: false,
    hideUrls: false,
  });
  const [template, setTemplate] = useState(templates.DAO);
  const [lookbookName, setLookbookName] = useState("");
  const [lookbookDate, setLookbookDate] = useState("");
  const [client, setClient] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const [highlightErrors, setHighlightErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [previewFileId, setPreviewFileId] = useState(null);
  const [pdfDataCache, setPdfDataCache] = useState(null);

  useEffect(() => {
    if (id === "new" || !lookbook?.data) {
      return;
    }

    // `?? []` matters: with optional chaining alone, a payload without
    // `listing_objects` set `listings` to undefined, and every
    // `Object.keys(listings)` call downstream (getHighlightsCount, goToProducts)
    // throws "Cannot convert undefined or null to object".
    setListings(
      (lookbook.data.listing_objects ?? []).reduce(
        (listings, listing) => ({
          ...listings,
          [listing.id]: listing,
        }),
        {}
      )
    );
    // Normalise to strings. The API returns numeric ids, but `Object.keys()`
    // yields strings, and the merge effect below compares the two with
    // `includes` — so unnormalised ids never matched and every listing got
    // pushed a second time, duplicating rows and React keys.
    setTimeout(
      () => setListingsOrder((lookbook.data.listings ?? []).map(String)),
      0
    );
    setSettings(lookbook.data.settings);
    setTemplate(lookbook.data.template);
    setLookbookName(lookbook.data.lookbookName);
    setLookbookDate(lookbook.data.lookbookDate);
    setHighlights(lookbook.data.highlights ?? []);
    setClient(lookbook.data.client);
    setPreviewFileId(lookbook.file_id);
    setPdfDataCache(
      JSON.stringify({
        template: lookbook.data.template,
        lookbookName: lookbook.data.lookbookName,
        lookbookDate: lookbook.data.lookbookDate,
        listings: lookbook.data.listings,
        highlights: lookbook.data.highlights,
        settings: lookbook.data.settings,
      })
    );
    setCurrentStep(steps.PREVIEW);
    setLastStep(steps.PREVIEW);
    setInitialized(true);
  }, [id, lookbook]);

  useEffect(() => {
    setListingsOrder((prevOrder) => {
      const knownIds = Object.keys(listings);
      // Both sides as strings — see the normalisation note in the hydrate
      // effect above. Comparing numeric ids against `Object.keys` strings made
      // `includes` always false.
      const updatedOrder = (prevOrder ?? []).map(String);
      knownIds.forEach((id) => {
        if (!updatedOrder.includes(id)) updatedOrder.push(id);
      });
      return updatedOrder.filter((id) => listings[id]);
    });
  }, [listings]);

  const updateSort = (newSort) => {
    setSort(newSort);
    setListingsOrder((prevOrder) => {
      const dir = newSort.endsWith("_desc") ? -1 : 1;
      const numeric = newSort.startsWith("price_");

      return prevOrder
        .map((listingId) => ({
          id: listingId,
          val: getValue(listings[listingId], newSort),
        }))
        .sort(
          ({ val: a }, { val: b }) =>
            dir * (numeric ? a - b : (a || "").localeCompare(b || ""))
        )
        .map(({ id }) => id);
    });
  };

  const getCollapsibleState = (step) => {
    let isVisible = stepsOrder.indexOf(step) <= stepsOrder.indexOf(lastStep);
    let isOpen = step === currentStep;

    return {
      isVisible,
      isOpen,
    };
  };

  const toggleCollapsible = (step) => {
    if (step === currentStep) {
      setCurrentStep(null);
    } else if (stepsOrder.indexOf(step) <= stepsOrder.indexOf(lastStep)) {
      setCurrentStep(step);
    }
  };

  const isListingSelected = (listing) => {
    return !!listings[listing.id];
  };

  const toggleListing = (listing) => {
    setListings((prevListings) => {
      const nextListings = { ...prevListings };

      if (prevListings[listing.id]) {
        delete nextListings[listing.id];
      } else {
        nextListings[listing.id] = listing;
      }

      return nextListings;
    });
  };

  const getHighlightsCount = () => {
    return Math.min(3, Math.ceil(Object.keys(listings).length / 12));
  };

  const goToStep = (step) => {
    if (stepsOrder.indexOf(step) > stepsOrder.indexOf(lastStep)) {
      setLastStep(step);
    }

    setCurrentStep(step);
  };

  const goToProducts = () => {
    if (Object.keys(listings).length === 0) {
      return;
    }

    goToStep(steps.PRODUCTS);
  };

  const goToHighlights = () => {
    goToStep(steps.HIGHLIGHTS);
  };

  const goToCover = () => {
    const highlightsCount = getHighlightsCount();

    let valid = true;
    for (let i = 0; i < highlightsCount; i++) {
      if (!highlighSchema.isValidSync(highlights[i])) {
        try {
          highlighSchema.validateSync(highlights[i], { abortEarly: false });
        } catch (e) {
          const errors = {};
          e.inner.forEach((error) => {
            errors[error.path] = error.message;
          });
          setHighlightErrors((prevErrors) => {
            return {
              ...prevErrors,
              [i]: errors,
            };
          });
        }
        valid = false;
      } else {
        setHighlightErrors((prevErrors) => {
          return {
            ...prevErrors,
            [i]: null,
          };
        });
      }
    }

    if (!valid) {
      return;
    }

    goToStep(steps.COVER);
  };

  const goToPreview = () => {
    goToStep(steps.PREVIEW);
  };

  const changeNote = (listingId, note) => {
    setSettings((prevSettings) => {
      return {
        ...prevSettings,
        [listingId]: {
          ...(prevSettings[listingId] || {}),
          note,
        },
      };
    });
  };

  const toggleBrandsVisibility = () => {
    setSettings((prevSettings) => {
      return {
        ...prevSettings,
        hideBrands: !prevSettings.hideBrands,
      };
    });
  };

  const toggleCommentsVisibility = () => {
    setSettings((prevSettings) => {
      return {
        ...prevSettings,
        hideComments: !prevSettings.hideComments,
      };
    });
  };

  const togglePricesVisibility = () => {
    setSettings((prevSettings) => {
      return {
        ...prevSettings,
        hidePrices: !prevSettings.hidePrices,
      };
    });
  };

  const toggleUrlsVisibility = () => {
    setSettings((prevSettings) => {
      return {
        ...prevSettings,
        hideUrls: !prevSettings.hideUrls,
      };
    });
  };

  const toggleListingHideUrl = (listingId) => {
    setSettings((prevSettings) => {
      return {
        ...prevSettings,
        [listingId]: {
          ...(prevSettings[listingId] || {}),
          hideUrl: !prevSettings[listingId]?.hideUrl,
        },
      };
    });
  };

  const onAssignClient = (assignedClient) => {
    setClient(assignedClient);
    goToPreview();
  };

  const createOrUpdateLookbook = async () => {
    if (!listingsOrder?.length) {
      return;
    }
    try {
      const response = await upsertLookbook({
        id,
        data: {
          file_id: previewFileId,
          assignedTo: client.id,
          template,
          lookbookName,
          lookbookDate,
          listings: listingsOrder,
          highlights,
          settings,
        },
      });

      setPreviewFileId(null);
      setSubmitting(false);
      history.push(`/lookbooks/${response.lookbook?.id}`);
    } catch (e) {
      console.error("PDF generation] ERROR", e);
    }
  };

  // Polled by Preview (usePoll) until the PDF's file_id exists. Quiet, and it
  // REJECTS on failure, so the poll can back off and stop on 403/404 and the
  // preview shows its own "Reconnecting…" line instead of a toast per tick.
  const updateLising = async () => {
    if (currentStep === steps.PREVIEW) {
      const response = await axios.get(`/api/lookbooks/${id}`, { metadata: { quiet: true } });
      setPreviewFileId(response?.data?.file_id);
    }
  };

  const savePdf = async () => {
    setSubmitting(true);

    downloadLookbook(id, previewFileId, lookbookName);
    setSubmitting(false);
    setTimeout(
      () => history.push(`/listings/lookbooks?${iframeQueryParams}`),
      1000
    );
  };

  return {
    lastStep,
    getCollapsibleState,
    toggleCollapsible,
    isListingSelected,
    toggleListing,
    goToProducts,
    goToHighlights,
    goToCover,
    sort,
    updateSort,
    listingsOrder,
    setListingsOrder,
    listings,
    lookbookName,
    lookbookDate,
    setLookbookName,
    setLookbookDate,
    settings,
    toggleBrandsVisibility,
    toggleCommentsVisibility,
    togglePricesVisibility,
    toggleUrlsVisibility,
    currentStep,
    brandsHidden: settings.hideBrands,
    commentsHidden: settings.hideComments,
    pricesHidden: settings.hidePrices,
    urlsHidden: settings.hideUrls,
    changeNote,
    toggleListingHideUrl,
    template,
    setTemplate,
    onAssignClient,
    getHighlightsCount,
    highlights,
    setHighlights,
    highlightErrors,
    setHighlightErrors,
    previewFileId,
    savePdf,
    submitting,
    isEditing: id !== "new",
    createOrUpdateLookbook,
    updateLising,
  };
});

export { UpsertLookbookProvider, useUpsertLookbook, stepsOrder };
